import { useState } from "react"

function getCsrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
}

export function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    interest: "signup",
    message: "",
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError("")
    try {
      const res = await fetch("/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrfToken() },
        credentials: "same-origin",
        body: JSON.stringify(form),
      })
      if (res.ok) {
        setSubmitted(true)
      } else {
        const data = await res.json().catch(() => ({}))
        setError(data.error ?? "Something went wrong. Please try again.")
      }
    } catch {
      setError("Network error. Please check your connection and try again.")
    } finally {
      setSubmitting(false)
    }
  }

  const inputClass =
    "w-full bg-white/[0.05] border border-white/10 rounded-md px-4 py-3 text-[14px] text-white/85 focus:border-[#2E7D4F]/50 outline-none transition-colors placeholder:text-white/20"

  return (
    <section id="contact" className="py-24 px-8 lg:px-12 bg-[#111820]">
      <div className="max-w-[1200px] mx-auto">
        <div className="grid lg:grid-cols-[1fr,1.2fr] gap-20 items-start">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#3B9B63] mb-4">
              Get Started
            </div>
            <h2
              className="text-[clamp(32px,4vw,48px)] font-bold leading-[1.12] text-white mb-5 tracking-[-0.02em]"
              style={{ fontFamily: "'Playfair Display', 'Cormorant Garamond', Georgia, serif" }}
            >
              Ready to get Organized?
            </h2>
            <p className="text-[17px] text-white/50 leading-[1.7] font-light mb-8">
              Get in touch to request access to LandTrack or learn more about how we can help your
              acquisition team.
            </p>
            <ul className="space-y-5">
              {[
                {
                  title: "Request Access",
                  desc: "Get a demo and see how LandTrack fits your workflow",
                },
                {
                  title: "Ask Questions",
                  desc: "Learn more about features, pricing, and implementation",
                },
                {
                  title: "Landman Services",
                  desc: "Need professional landman support? We can help with that too",
                },
              ].map((item) => (
                <li key={item.title} className="flex gap-4 items-start">
                  <div className="w-2 h-2 bg-[#2E7D4F] rounded-full mt-[7px] flex-shrink-0" />
                  <div>
                    <strong className="text-white/85 font-medium text-[14px] block mb-[2px]">
                      {item.title}
                    </strong>
                    <span className="text-[14px] text-white/55 leading-[1.6] font-light">
                      {item.desc}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#1A2332] border border-white/[0.08] rounded-xl p-9">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-14 h-14 bg-[#2E7D4F]/20 rounded-full flex items-center justify-center mb-5">
                  <svg
                    className="w-7 h-7 text-[#3B9B63]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-white text-[20px] font-semibold mb-2">Message Sent</h3>
                <p className="text-white/50 text-[15px] leading-[1.6]">
                  Thanks for reaching out. We'll be in touch shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} action="/contact" method="POST" className="space-y-5">
                <div>
                  <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase text-white/40 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputClass}
                    placeholder="John Smith"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase text-white/40 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={inputClass}
                    placeholder="john@company.com"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase text-white/40 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      className={inputClass}
                      placeholder="Company Name"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase text-white/40 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className={inputClass}
                      placeholder="(405) 555-0123"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase text-white/40 mb-2">
                    I'm Interested In
                  </label>
                  <select
                    value={form.interest}
                    onChange={(e) => setForm({ ...form, interest: e.target.value })}
                    className={inputClass + " cursor-pointer"}
                  >
                    <option value="signup">LandTrack Access</option>
                    <option value="demo">Request a Demo</option>
                    <option value="landman">Landman Services</option>
                    <option value="general">General Inquiry</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase text-white/40 mb-2">
                    Message
                  </label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={4}
                    className={inputClass + " resize-none"}
                    placeholder="Tell us about your needs..."
                  />
                </div>
                {error && <p className="text-red-400 text-[13px]">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#2E7D4F] hover:bg-[#3B9B63] disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-md py-[14px] text-[14px] font-semibold tracking-[0.04em] transition-colors mt-4"
                >
                  {submitting ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
